import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useQueryClient } from "@tanstack/react-query";

export function usePaystackVerification() {
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userRole } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    const verifyPayment = async (reference: string) => {
      if (!user?.id || isVerifying || isVerified) return;

      // Check if this is a verification payment - if so, don't handle it here
      const params = new URLSearchParams(location.search);
      const isVerification =
        params.get("verification") === "true" ||
        location.pathname.includes("verification/success") ||
        reference.startsWith("verification-");

      if (isVerification) {
        console.log(
          `🎯 [PAYSTACK_VERIFICATION] Skipping verification payment ${reference} - handled by VerificationSuccess page`,
        );
        return;
      }

      // Store a verification marker in sessionStorage to prevent duplicate verifications
      const verificationKey = `paystack-verification-${reference}`;
      const alreadyVerifying = sessionStorage.getItem(verificationKey);
      if (alreadyVerifying === "true") {
        console.log(
          `🎯 [PAYSTACK_VERIFICATION] Skipping duplicate verification for ${verificationKey}`,
        );
        return;
      }

      sessionStorage.setItem(verificationKey, "true");
      setIsVerifying(true);

      try {
        console.log(
          `🎯 [PAYSTACK_VERIFICATION] Verifying wallet top-up payment reference: ${reference} for user: ${user.id}`,
        );

        // Use paystack-payment endpoint for wallet top-ups
        const { data, error } = await supabase.functions.invoke(
          "paystack-payment",
          {
            body: {
              reference,
              userId: user.id,
              verification: false,
            },
          },
        );

        if (error) {
          console.error(
            "❌ [PAYSTACK_VERIFICATION] Payment verification error:",
            error,
          );
          toast({
            title: "Verification Failed",
            description:
              "Failed to verify payment. Please contact support with your reference number.",
            variant: "destructive",
          });
          setIsVerifying(false);
          sessionStorage.removeItem(verificationKey);
          return;
        }

        console.log(
          "✅ [PAYSTACK_VERIFICATION] Payment verification response:",
          data,
        );

        if (data.status === true) {
          toast({
            title: "Payment Successful",
            description: "Payment successful! Your wallet has been topped up.",
            variant: "default",
          });
          // Invalidate relevant queries to refresh wallet data
          queryClient.invalidateQueries({ queryKey: ["sponsorWallet"] });
          queryClient.invalidateQueries({ queryKey: ["transactions"] });
          queryClient.invalidateQueries({ queryKey: ["wallet"] });

          // Redirect to dashboard
          setTimeout(() => {
            navigate(
              `/dashboard/${userRole === "artist" ? "artist" : "sponsor"}?payment_success=true`,
              { replace: true },
            );
          }, 1500);

          setIsVerified(true);
        } else {
          console.log(
            "❌ [PAYSTACK_VERIFICATION] Payment not successful:",
            data,
          );
          toast({
            title: "Verification Failed",
            description:
              data.message ||
              "Payment verification failed. Please contact support with your reference number.",
            variant: "destructive",
          });
        }
      } catch (err) {
        console.error(
          "❌ [PAYSTACK_VERIFICATION] Error during payment verification:",
          err,
        );
        toast({
          title: "Error",
          description:
            "An unexpected error occurred while verifying your payment",
          variant: "destructive",
        });
      } finally {
        setIsVerifying(false);
        sessionStorage.removeItem(verificationKey);

        // Clear the URL parameters after verification
        if (!isVerified && location.search) {
          const cleanPath = location.pathname;
          window.history.replaceState({}, document.title, cleanPath);
        }
      }
    };

    // Extract reference from URL if present
    const params = new URLSearchParams(location.search);
    const reference = params.get("ref");

    // Only process if this is NOT a verification payment and NOT a Flutterwave payment
    const isFlutterwavePayment =
      reference &&
      (reference.startsWith("flw-") ||
        params.has("tx_ref") ||
        params.get("gateway") === "flutterwave");

    const isVerificationPayment =
      reference &&
      (reference.startsWith("verification-") ||
        params.get("verification") === "true" ||
        location.pathname.includes("verification/success"));

    if (
      reference &&
      !isFlutterwavePayment &&
      !isVerificationPayment &&
      user?.id
    ) {
      console.log(
        `🔗 [PAYSTACK_VERIFICATION] Found wallet top-up payment reference in URL: ${reference}`,
      );
      verifyPayment(reference);
    }
  }, [
    location.search,
    location.pathname,
    user,
    queryClient,
    isVerifying,
    isVerified,
    navigate,
    userRole,
  ]);

  return { isVerifying, isVerified };
}
